import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { LoginModal } from '@/components/auth/LoginModal';
import { Button } from '@/components/ui/button';
import {
  Search, SlidersHorizontal, MapPin, Briefcase, Clock, Euro,
  ChevronDown, ChevronLeft, ChevronRight, X, User, Star,
  Monitor, Building2, Loader2, Filter, MessageCircle, Heart
} from 'lucide-react';
import { toast } from 'sonner';

// ─── Star Rating Display ────────────────
const StarRating = ({ rating, count }) => {
  if (!count) return null;
  return (
    <div className="flex items-center gap-1" data-testid="star-rating">
      <div className="flex">
        {[1, 2, 3, 4, 5].map(i => (
          <Star
            key={i}
            size={13}
            className={i <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}
          />
        ))}
      </div>
      <span className="text-xs font-medium text-foreground">{rating}</span>
      <span className="text-xs text-muted-foreground">({count})</span>
    </div>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Expert Card ────────────────────────
const ExpertCard = ({ expert, onContact, contactLoading, isFavorited, onToggleFavorite, isEnterprise }) => {
  const availabilityLabels = {
    immediate: 'Immédiate',
    available: 'Disponible',
    flexible: 'Flexible',
    '1_week': 'Sous 1 sem.',
    '2_weeks': 'Sous 2 sem.',
    '1_month': 'Sous 1 mois',
  };
  const workModeLabels = {
    remote: 'Télétravail',
    onsite: 'Sur site',
    hybrid: 'Hybride',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border rounded-xl overflow-hidden hover:border-primary/30 hover:shadow-lg transition-all group"
      data-testid={`expert-card-${expert.id}`}
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start gap-3.5 mb-4">
          {expert.photo_url ? (
            <img src={expert.photo_url} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center flex-shrink-0">
              <User size={24} className="text-primary" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
              {expert.name}
            </h3>
            <p className="text-sm text-muted-foreground truncate">{expert.title || 'Expert'}</p>
            {expert.review_count > 0 && (
              <StarRating rating={expert.avg_rating} count={expert.review_count} />
            )}
            {expert.location && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                <MapPin size={11} /> {expert.location}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
            {isEnterprise && (
              <button
                onClick={(e) => { e.stopPropagation(); onToggleFavorite(expert); }}
                className={`p-1.5 rounded-lg transition-all ${isFavorited ? 'text-red-500 bg-red-50 hover:bg-red-100' : 'text-muted-foreground hover:text-red-400 hover:bg-muted'}`}
                data-testid={`favorite-btn-${expert.id}`}
                title={isFavorited ? 'Retirer des favoris' : 'Ajouter aux favoris'}
              >
                <Heart size={18} fill={isFavorited ? 'currentColor' : 'none'} />
              </button>
            )}
            {expert.hourly_rate && (
              <div className="text-right">
                <p className="text-lg font-bold text-foreground">{expert.hourly_rate}&euro;</p>
                <p className="text-xs text-muted-foreground">/heure</p>
              </div>
            )}
          </div>
        </div>

        {/* Bio */}
        {expert.bio && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{expert.bio}</p>
        )}

        {/* Skills */}
        {expert.skills?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {expert.skills.slice(0, 4).map((skill, i) => (
              <span key={i} className="px-2 py-0.5 bg-primary/8 text-primary text-xs rounded-md font-medium">
                {skill}
              </span>
            ))}
            {expert.skills.length > 4 && (
              <span className="px-2 py-0.5 bg-secondary text-muted-foreground text-xs rounded-md">
                +{expert.skills.length - 4}
              </span>
            )}
          </div>
        )}

        {/* Metadata */}
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground pt-3 border-t border-border">
          {expert.experience_years && (
            <span className="flex items-center gap-1">
              <Briefcase size={12} /> {expert.experience_years} ans
            </span>
          )}
          {expert.availability && (
            <span className="flex items-center gap-1">
              <Clock size={12} /> {availabilityLabels[expert.availability] || expert.availability}
            </span>
          )}
          {expert.work_mode && (
            <span className="flex items-center gap-1">
              <Monitor size={12} /> {workModeLabels[expert.work_mode] || expert.work_mode}
            </span>
          )}
          {expert.languages?.length > 0 && (
            <span className="flex items-center gap-1">
              {expert.languages.slice(0, 2).join(', ')}
            </span>
          )}
        </div>

        {/* Contact Button */}
        <Button
          size="sm"
          className="w-full mt-4"
          onClick={() => onContact(expert)}
          disabled={contactLoading === expert.id}
          data-testid={`contact-expert-${expert.id}`}
        >
          {contactLoading === expert.id ? (
            <Loader2 size={14} className="animate-spin mr-2" />
          ) : (
            <MessageCircle size={14} className="mr-2" />
          )}
          Contacter cet expert
        </Button>
      </div>
    </motion.div>
  );
};

// ─── Filter Chip ────────────────────────
const FilterChip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary text-xs rounded-full font-medium">
    {label}
    <button onClick={onRemove} className="hover:text-primary/70"><X size={12} /></button>
  </span>
);

// ════════════════════════════════════════
// EXPERT SEARCH PAGE
// ════════════════════════════════════════
const ExpertSearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [results, setResults] = useState({ items: [], total: 0, page: 1, pages: 1, facets: {} });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [contactLoading, setContactLoading] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState(new Set());

  // Check if current user is enterprise
  const getUser = () => {
    try { return JSON.parse(localStorage.getItem('sfretire_user') || 'null'); } catch { return null; }
  };
  const currentUser = getUser();
  const isEnterprise = currentUser?.role === 'entreprise' || currentUser?.user_type === 'entreprise';

  // Fetch favorite IDs on mount (enterprise only)
  useEffect(() => {
    if (!isEnterprise) return;
    const token = localStorage.getItem('sfretire_token');
    if (!token) return;
    fetch(`${API_URL}/api/favorites/ids`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : { ids: [] })
      .then(data => setFavoriteIds(new Set(data.ids)))
      .catch(() => {});
  }, [isEnterprise]);

  // Filters state
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [sector, setSector] = useState(searchParams.get('sector') || '');
  const [skills, setSkills] = useState(searchParams.get('skills') || '');
  const [minRate, setMinRate] = useState(searchParams.get('min_rate') || '');
  const [maxRate, setMaxRate] = useState(searchParams.get('max_rate') || '');
  const [availability, setAvailability] = useState(searchParams.get('availability') || '');
  const [workMode, setWorkMode] = useState(searchParams.get('work_mode') || '');
  const [location, setLocation] = useState(searchParams.get('location') || '');
  const [minExperience, setMinExperience] = useState(searchParams.get('min_experience') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort_by') || 'relevance');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));

  const fetchResults = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (sector) params.set('sector', sector);
    if (skills) params.set('skills', skills);
    if (minRate) params.set('min_rate', minRate);
    if (maxRate) params.set('max_rate', maxRate);
    if (availability) params.set('availability', availability);
    if (workMode) params.set('work_mode', workMode);
    if (location) params.set('location', location);
    if (minExperience) params.set('min_experience', minExperience);
    if (sortBy) params.set('sort_by', sortBy);
    params.set('page', page);
    params.set('per_page', '12');

    try {
      const res = await fetch(`${API_URL}/api/experts/search?${params}`);
      if (res.ok) {
        const data = await res.json();
        setResults(data);
      }
    } catch (e) {
      console.error('Search error:', e);
    } finally {
      setLoading(false);
    }
  }, [query, sector, skills, minRate, maxRate, availability, workMode, location, minExperience, sortBy, page]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const handleSearch = (e) => {
    e?.preventDefault();
    setPage(1);
    fetchResults();
  };

  const clearFilters = () => {
    setQuery(''); setSector(''); setSkills(''); setMinRate(''); setMaxRate('');
    setAvailability(''); setWorkMode(''); setLocation(''); setMinExperience('');
    setSortBy('relevance'); setPage(1);
  };

  const handleContact = async (expert) => {
    const token = localStorage.getItem('sfretire_token');
    const storedUser = localStorage.getItem('sfretire_user');

    // Not logged in — show login modal
    if (!token || !storedUser) {
      setShowLoginModal(true);
      return;
    }

    const user = JSON.parse(storedUser);
    // Not an enterprise — inform
    if (user.role !== 'entreprise' && user.user_type !== 'entreprise') {
      toast.error('Seules les entreprises peuvent contacter un expert. Connectez-vous avec un compte entreprise.');
      return;
    }

    setContactLoading(expert.id);
    try {
      const res = await fetch(`${API_URL}/api/conversations/direct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ retraite_id: expert.id }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur lors de la création de la conversation');
      }

      const data = await res.json();
      toast.success(`Conversation ouverte avec ${expert.name}`);
      navigate(`/messages/${data.conversation.id}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setContactLoading(null);
    }
  };

  const handleToggleFavorite = async (expert) => {
    const token = localStorage.getItem('sfretire_token');
    if (!token || !isEnterprise) return;

    const isFav = favoriteIds.has(expert.id);
    // Optimistic update
    setFavoriteIds(prev => {
      const next = new Set(prev);
      isFav ? next.delete(expert.id) : next.add(expert.id);
      return next;
    });

    try {
      const res = await fetch(`${API_URL}/api/favorites/${expert.id}`, {
        method: isFav ? 'DELETE' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success(isFav ? `${expert.name} retiré des favoris` : `${expert.name} ajouté aux favoris`);
    } catch {
      // Revert on error
      setFavoriteIds(prev => {
        const next = new Set(prev);
        isFav ? next.add(expert.id) : next.delete(expert.id);
        return next;
      });
      toast.error('Erreur lors de la mise à jour des favoris');
    }
  };

  const activeFilterCount = [sector, skills, minRate, maxRate, availability, workMode, location, minExperience]
    .filter(Boolean).length;

  const inputCls = "w-full h-9 px-3 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary";

  return (
    <div className="min-h-screen bg-background" data-testid="expert-search-page">
      <Navbar />

      <main className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-serif font-bold text-foreground">
              Trouver un <span className="text-primary">expert</span>
            </h1>
            <p className="text-muted-foreground mt-2">
              {results.total} expert{results.total !== 1 ? 's' : ''} disponible{results.total !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="mb-6">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Rechercher par nom, compétence, titre, entreprise..."
                  className="w-full h-12 pl-11 pr-4 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" className="h-12 px-6" data-testid="search-btn">
                <Search size={18} className="mr-2" /> Rechercher
              </Button>
              <Button
                type="button"
                variant="outline"
                className={`h-12 px-4 ${activeFilterCount > 0 ? 'border-primary text-primary' : ''}`}
                onClick={() => setShowFilters(!showFilters)}
                data-testid="toggle-filters-btn"
              >
                <SlidersHorizontal size={18} className="mr-2" />
                Filtres
                {activeFilterCount > 0 && (
                  <span className="ml-1.5 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </div>
          </form>

          {/* Active Filters */}
          {activeFilterCount > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="active-filters">
              {sector && <FilterChip label={`Secteur: ${sector}`} onRemove={() => setSector('')} />}
              {skills && <FilterChip label={`Compétences: ${skills}`} onRemove={() => setSkills('')} />}
              {location && <FilterChip label={`Lieu: ${location}`} onRemove={() => setLocation('')} />}
              {minRate && <FilterChip label={`Min: ${minRate}EUR/h`} onRemove={() => setMinRate('')} />}
              {maxRate && <FilterChip label={`Max: ${maxRate}EUR/h`} onRemove={() => setMaxRate('')} />}
              {availability && <FilterChip label={`Dispo: ${availability}`} onRemove={() => setAvailability('')} />}
              {workMode && <FilterChip label={`Mode: ${workMode}`} onRemove={() => setWorkMode('')} />}
              {minExperience && <FilterChip label={`${minExperience}+ ans exp.`} onRemove={() => setMinExperience('')} />}
              <button onClick={clearFilters} className="text-xs text-muted-foreground hover:text-foreground underline">
                Tout effacer
              </button>
            </div>
          )}

          {/* Filters Panel */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden mb-6"
              >
                <div className="bg-card border border-border rounded-xl p-5" data-testid="filters-panel">
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Sector */}
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Secteur</label>
                      <select value={sector} onChange={e => { setSector(e.target.value); setPage(1); }}
                        className={inputCls} data-testid="filter-sector">
                        <option value="">Tous les secteurs</option>
                        {(results.facets?.sectors || []).map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>

                    {/* Skills */}
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Compétences</label>
                      <input type="text" value={skills} onChange={e => setSkills(e.target.value)}
                        placeholder="Finance, Management..." className={inputCls} data-testid="filter-skills" />
                    </div>

                    {/* Location */}
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Localisation</label>
                      <select value={location} onChange={e => { setLocation(e.target.value); setPage(1); }}
                        className={inputCls} data-testid="filter-location">
                        <option value="">Toutes les villes</option>
                        {(results.facets?.locations || []).map(l => (
                          <option key={l} value={l}>{l}</option>
                        ))}
                      </select>
                    </div>

                    {/* Min Experience */}
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Expérience min.</label>
                      <select value={minExperience} onChange={e => { setMinExperience(e.target.value); setPage(1); }}
                        className={inputCls} data-testid="filter-experience">
                        <option value="">Toute expérience</option>
                        <option value="5">5+ ans</option>
                        <option value="10">10+ ans</option>
                        <option value="15">15+ ans</option>
                        <option value="20">20+ ans</option>
                        <option value="30">30+ ans</option>
                      </select>
                    </div>

                    {/* Rate Range */}
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Tarif horaire (EUR)</label>
                      <div className="flex gap-2">
                        <input type="number" min="0" value={minRate} onChange={e => setMinRate(e.target.value)}
                          placeholder="Min" className={inputCls} data-testid="filter-min-rate" />
                        <input type="number" min="0" value={maxRate} onChange={e => setMaxRate(e.target.value)}
                          placeholder="Max" className={inputCls} data-testid="filter-max-rate" />
                      </div>
                    </div>

                    {/* Availability */}
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Disponibilité</label>
                      <select value={availability} onChange={e => { setAvailability(e.target.value); setPage(1); }}
                        className={inputCls} data-testid="filter-availability">
                        <option value="">Toutes</option>
                        <option value="immediate">Immédiate</option>
                        <option value="flexible">Flexible</option>
                        <option value="1_week">Sous 1 semaine</option>
                        <option value="2_weeks">Sous 2 semaines</option>
                        <option value="1_month">Sous 1 mois</option>
                      </select>
                    </div>

                    {/* Work Mode */}
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Mode de travail</label>
                      <select value={workMode} onChange={e => { setWorkMode(e.target.value); setPage(1); }}
                        className={inputCls} data-testid="filter-work-mode">
                        <option value="">Tous les modes</option>
                        <option value="remote">Télétravail</option>
                        <option value="onsite">Sur site</option>
                        <option value="hybrid">Hybride</option>
                      </select>
                    </div>

                    {/* Sort */}
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Trier par</label>
                      <select value={sortBy} onChange={e => { setSortBy(e.target.value); setPage(1); }}
                        className={inputCls} data-testid="filter-sort">
                        <option value="relevance">Pertinence</option>
                        <option value="rate_asc">Tarif croissant</option>
                        <option value="rate_desc">Tarif décroissant</option>
                        <option value="experience">Expérience</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                    <Button size="sm" onClick={handleSearch} data-testid="apply-filters-btn">
                      <Filter size={14} className="mr-1" /> Appliquer
                    </Button>
                    <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                      Réinitialiser
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Results */}
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="animate-spin text-primary" size={40} />
            </div>
          ) : results.items.length === 0 ? (
            <div className="text-center py-20 bg-card border border-border rounded-xl" data-testid="no-results">
              <Search size={48} className="mx-auto mb-4 text-muted-foreground/30" />
              <h2 className="text-xl font-semibold text-foreground mb-2">Aucun expert trouvé</h2>
              <p className="text-muted-foreground mb-4">Essayez de modifier vos critères de recherche</p>
              <Button variant="outline" onClick={clearFilters}>Réinitialiser les filtres</Button>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="results-grid">
                {results.items.map(expert => (
                  <ExpertCard
                    key={expert.id}
                    expert={expert}
                    onContact={handleContact}
                    contactLoading={contactLoading}
                    isFavorited={favoriteIds.has(expert.id)}
                    onToggleFavorite={handleToggleFavorite}
                    isEnterprise={isEnterprise}
                  />
                ))}
              </div>

              {/* Pagination */}
              {results.pages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-10" data-testid="pagination">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    <ChevronLeft size={16} /> Précédent
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page} sur {results.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= results.pages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Suivant <ChevronRight size={16} />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />

      {/* Login Modal for non-authenticated users */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        userType="entreprise"
        onSwitchToRegister={() => {
          setShowLoginModal(false);
          window.location.href = '/entreprises';
        }}
      />
    </div>
  );
};

export default ExpertSearchPage;
